import React, { Fragment, useState, useCallback, FC } from 'react';
import {
  ReferenceManyField,
  useTranslate,
  Filter,
  TextField,
  Datagrid,
  useGetList,
} from 'react-admin';
import Grid from '@material-ui/core/Grid';
import { IconButton } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { debounce, set as lodashSet } from 'lodash';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import cx from 'classnames';

import { NoPermissions, StatusField } from '~/components/ra';
import {
  TextInput,
  PhoneNumberInput,
  ChipField,
  AddFilterAction,
  SelectInput as CustomSelectInput,
} from '~/components';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import AccountTableSingleSelect from '~/components/AccountTableSingleSelect/AccountTableSingleSelect';
import TerminalShow from './TerminalShow';
import TerminalCreate from './TerminalCreate';
import { hasTranslation, Longdash } from '~/utils';
import RaPagination from '~/components/Pagination/RaPagination';
import usePath from '~/hooks/usePath';
import { FunctionField } from 'react-admin';
import { Terminal, Business } from '~/types';
import ReferenceField from '~/components/ReferenceField';
import { rowsPerPageOptionsDefault, perPageDefault } from '~/utils/pagination';
import { useAccountPermissions } from '~/hooks/accounts';

const label = (key: string): string => `resources.businesses.terminals.${key}`;

interface TerminalsListProps {
  record: Business;
  permissions: string[];
  resource: 'businesses';
  [x: string]: any;
}

const TerminalsList: FC<TerminalsListProps> = (props) => {
  const { record, permissions, resource, ...rest } = props;
  const translate = useTranslate();
  const t = (key: string): string => translate(label(key));

  const [showData, setShowData] = useState(null);
  const handleOpen = (data: any) => {
    setShowData(data);
  };
  const handleClose = () => setShowData(null);

  const [filters, setFilters] = useState<any>({});
  const classes = useStyles();

  const { hasListPermission } = useAccountPermissions('accounts', 'businesses');

  if (!record) return null;
  if (!permissions) return null;
  if (!permissions.includes('business.terminal.list')) return <NoPermissions />;

  return (
    <Fragment>
      <TerminalShow
        showData={showData}
        onClose={handleClose}
        businessRecord={record}
        permissions={permissions}
        parentProps={rest}
      />
      <Grid container justify='space-between' alignItems='center'>
        <Grid item className={classes.filters}>
          <Filters setFilters={setFilters} filters={filters} record={record} resource={resource} />
        </Grid>
        <Grid item className={classes.addBtn}>
          <TerminalCreate permissions={permissions} businessRecord={record} />
        </Grid>
      </Grid>
      <ReferenceManyField
        label='Terminals'
        reference='terminals'
        target='businesses'
        pagination={
          <RaPagination
            defaultPerPage={perPageDefault}
            rowsPerPageOptions={rowsPerPageOptionsDefault}
          />
        }
        perPage={perPageDefault}
        filter={filters}
        {...props}
        record={record}
      >
        <Datagrid rowClick={(_id: string, _basePath: string, record: any) => handleOpen(record)}>
          <TextField label={t('label')} source='label' sortable={false} />
          <FunctionField
            label={t('type')}
            render={(record: Terminal) =>
              record?.terminalType && hasTranslation(label(record?.terminalType))
                ? t(record?.terminalType)
                : record?.terminalType
            }
            sortable={false}
          />
          {hasListPermission && (
            <FunctionField
              label={t('accounts')}
              render={(record: Terminal) => {
                const accountIds = record?.accountIds || [];
                if (accountIds.length === 0) return null;
                const firstAccId = accountIds[0];
                const restCount = accountIds.length - 1;
                return (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <ReferenceField
                      record={{ id: firstAccId, type: 'ACCOUNT' }}
                      withLabel={false}
                      withLink={false}
                      renderer={(account) => (
                        <div style={{ fontWeight: 400 }}>{account?.number}</div>
                      )}
                    />
                    {restCount > 0 && (
                      <ChipField
                        label={`+${restCount}`}
                        style={{ marginLeft: 8, cursor: 'pointer' }}
                      />
                    )}
                  </div>
                );
              }}
              sortable={false}
            />
          )}
          <FunctionField
            label={t('location')}
            render={(record: Terminal) => {
              if (!record.locationId) {
                return null;
              } else {
                return (
                  <ReferenceField
                    record={{ id: record.locationId, type: 'LOCATION' }}
                    withLabel={false}
                    withLink={false}
                    renderer={(location) => (
                      <div style={{ fontWeight: 400, whiteSpace: 'normal' }}>{location?.name}</div>
                    )}
                  />
                );
              }
            }}
            sortable={false}
          />
          <TextField label={t('linkedPhone')} source='linkedPhone' sortable={false} />
          <TextField label={t('device._')} source='device.deviceLabel' sortable={false} />
          <StatusField label={t('status._')} sortable={false} />
        </Datagrid>
      </ReferenceManyField>
    </Fragment>
  );
};

const Filters = (props: any) => {
  const { setFilters, record } = props;
  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.businesses.terminals.${key}`);
  const { changePath } = usePath();
  const classes = useStyles();
  const listFilterClasses = useListFilterStyles();

  const { hasListPermission: hasAccountListPermission } = useAccountPermissions(
    'accounts',
    'businesses'
  );

  const [addedFilters, setAddedFilters] = useState<string[]>([]);

  const { ids: locationIds, data: locationsData = {} } = useGetList(
    'locations',
    { page: 1, perPage: 100 },
    { field: 'name', order: 'ASC' },
    { businessId: record?.id }
  );

  const debouncedSetFilters = useCallback(debounce(setFilters, 400), [setFilters]);

  const handleChange = (e: any) => {
    if (!e?.target) return null;
    const { name, value } = e.target;
    changePath({ page: 1 });
    debouncedSetFilters((prev: any) => ({ ...lodashSet(prev, name, value) }));
  };

  const handlePhoneChange = (value: string) => {
    changePath({ page: 1 });
    debouncedSetFilters((prev: any) => ({ ...lodashSet(prev, 'linkedPhone', value) }));
  };

  const hideFilter = (key: string) => {
    setFilters((prev: any) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setAddedFilters((prev) => prev.filter((f) => f !== key));
  };

  const allOptionalFilters = [
    'terminalType',
    'locationId',
    'linkedPhone',
    ...(hasAccountListPermission && record ? ['accountNumber'] : []),
  ];

  const renderAddedFilter = (key: string) => {
    switch (key) {
      case 'terminalType':
        return (
          <CustomSelectInput
            name='terminalType'
            label={t('type')}
            value={props.filters?.terminalType || ''}
            onChange={handleChange}
            allowEmpty
            emptyText={Longdash}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            options={[
              { value: 'MPOS', text: t('MPOS') },
              { value: 'ECOMMERCE', text: t('ECOMMERCE') },
            ]}
          />
        );
      case 'locationId':
        return (
          <CustomSelectInput
            name='locationId'
            label={t('locationId')}
            value={props.filters?.locationId || ''}
            onChange={handleChange}
            allowEmpty
            emptyText={Longdash}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            options={
              locationIds?.map((id: any) => ({ value: id, text: locationsData[id]?.name })) || []
            }
          />
        );
      case 'linkedPhone':
        return (
          <PhoneNumberInput
            name='linkedPhone'
            source='linkedPhone'
            label={t('linkedPhone')}
            onChange={handlePhoneChange}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'accountNumber':
        return record ? (
          <AccountTableSingleSelect
            resource='businesses'
            profileId={record.id}
            label={t('accountNumber')}
            value={props.filters?.accountNumber || ''}
            onChange={(account) => {
              changePath({ page: 1 });
              setFilters((prev: any) => ({ ...prev, accountNumber: account.number }));
            }}
            onClear={() => {
              changePath({ page: 1 });
              setFilters((prev: any) => ({ ...prev, accountNumber: undefined }));
            }}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        ) : null;
      default:
        return null;
    }
  };

  const renderAddedFilters = () =>
    addedFilters.map((filter) => {
      const component = renderAddedFilter(filter);
      if (!component) return null;
      return (
        <div className={listFilterClasses.flexContainer} key={filter}>
          {component}
          <IconButton
            aria-label='delete'
            className={listFilterClasses.deleteIcon}
            onClick={() => hideFilter(filter)}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      );
    });

  return (
    <div className={listFilterClasses.container}>
      <div className={listFilterClasses.inputsGroup}>
        <Filter
          className={classes.form}
          record={record}
          setFilters={() => null}
          hideFilter={() => null}
        >
          <TextInput
            name='label'
            source='label'
            label={t('label')}
            alwaysOn
            onChange={handleChange}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
            disableHelperText
          />
          <CustomSelectInput
            name='status'
            label={t('status._')}
            value={props.filters?.status || ''}
            onChange={handleChange}
            allowEmpty
            emptyText={Longdash}
            alwaysOn
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
            options={[
              { value: 'ACTIVE', text: t('status.ACTIVE') },
              { value: 'NOT_ACTIVATED', text: t('status.NOT_ACTIVATED') },
              { value: 'CLOSED', text: t('status.CLOSED') },
            ]}
          />
        </Filter>
        {renderAddedFilters()}
      </div>
      <div style={{ flexShrink: 0 }}>
        <AddFilterAction
          filters={allOptionalFilters
            .filter((f) => !addedFilters.includes(f))
            .map((key) => ({ key, label: t(key === 'terminalType' ? 'type' : key) }))}
          onFilterSelected={(filter: string) => setAddedFilters((prev) => [...prev, filter])}
        />
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  form: {
    alignItems: 'flex-start',
    '& > div': {
      padding: 0,
      '& > div:last-child': {
        width: 0,
      },
    },
  },
  addBtn: {
    alignSelf: 'flex-end',
    marginBottom: theme.spacing(1),
  },
  filters: {
    flex: 1,
  },
}));

export default TerminalsList;
